import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";

export interface IPartProConnectionStatus {
  connected: boolean;
  vendorId?: string;
  scopes?: string;
  connectedAt?: string;
  connectedByUserId?: string;
  accessTokenExpires?: string;
}

export interface IPartProCategory {
  id: string;
  name: string;
  slug: string;
  parentId?: string | null;
  shopifyCategoryId?: string | null;
  parent?: { id: string; name: string; slug: string } | null;
}

export interface IPartProShippingPolicy {
  id: string;
  name: string;
  shippingBand: string;
  pricePence: number;
  handlingDays: number;
  transitDays: number;
}

export default function usePartPro() {
  const { get, post, del } = useApiHelper();
  const queryClient = useQueryClient();

  const connection = useQuery({
    queryKey: ["partpro", "connection"],
    queryFn: () =>
      get<DefaultResponseWithData<IPartProConnectionStatus>>("/partpro/connection"),
  });

  const categories = useQuery({
    queryKey: ["partpro", "categories"],
    queryFn: () =>
      get<DefaultResponseWithData<IPartProCategory[]>>("/partpro/categories"),
    enabled: !!connection.data?.data?.connected,
  });

  const shippingPolicies = useQuery({
    queryKey: ["partpro", "shipping-policies"],
    queryFn: () =>
      get<DefaultResponseWithData<IPartProShippingPolicy[]>>("/partpro/shipping-policies"),
    enabled: !!connection.data?.data?.connected,
  });

  const getAuthorizeUrl = useMutation({
    mutationFn: (state: string) =>
      get<DefaultResponseWithData<string>>(`/partpro/authorize-url?state=${encodeURIComponent(state)}`),
  });

  const syncShippingPolicies = useMutation({
    mutationFn: () =>
      post<Record<string, never>, DefaultResponseWithData<IPartProShippingPolicy[]>>(
        "/partpro/shipping-policies/sync",
        {}
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partpro", "shipping-policies"] });
    },
  });

  const disconnect = useMutation({
    mutationFn: () => del<DefaultResponseWithData<unknown>>("/partpro/connection"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partpro"] });
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      queryClient.invalidateQueries({ queryKey: ["part", "marketplaces"] });
      // Part Requests hides its PartPro actions on this
      queryClient.invalidateQueries({ queryKey: ["partRequests", "status"] });
    },
  });

  return {
    connection,
    categories,
    shippingPolicies,
    getAuthorizeUrl,
    syncShippingPolicies,
    disconnect,
  };
}
